import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
} from "@tanstack/react-query";
import { SourceImagePresetApi } from "../api/source-image-preset-api";
import {
  UpdateSourceImagePresetDto,
  PagedSourceImagePresetsResponseDto,
} from "@repo/api/source-image-preset";
import { queryClient } from "./query-client";
import Toast from "../components/Toast";

export type SourceImagePresetsFilterParams = {
  tags?: string[];
  isFavourite?: boolean;
};

type SourceImagePresetCursor =
  | { createdAt: string; externalId: string }
  | undefined;

export function usePagedSourceImagePresetsQuery(
  teamExternalId: string,
  filterParams: SourceImagePresetsFilterParams,
  pageSize: number,
) {
  return useInfiniteQuery({
    queryKey: sourceImagePresetQueryKeys.paged(
      teamExternalId,
      filterParams,
      pageSize,
    ),
    queryFn: ({ pageParam }) =>
      SourceImagePresetApi.getPagedSourceImagePresets(teamExternalId, {
        ...filterParams,
        pageSize,
        cursorCreatedAt: pageParam?.createdAt,
        cursorExternalId: pageParam?.externalId,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as SourceImagePresetCursor,
    staleTime: 0,
    meta: {
      errorMessage: "Failed to fetch image presets",
    },
  });
}

export function useUpdateSourceImagePresetMutation({
  teamExternalId,
  onSuccess,
}: {
  teamExternalId: string;
  onSuccess?: () => void;
}) {
  return useMutation({
    mutationKey:
      sourceImagePresetQueryKeys.updateSourceImagePreset(teamExternalId),
    mutationFn: ({
      presetExternalId,
      data,
    }: {
      presetExternalId: string;
      data: UpdateSourceImagePresetDto;
    }) =>
      SourceImagePresetApi.updateSourceImagePreset(
        teamExternalId,
        presetExternalId,
        data,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: sourceImagePresetQueryKeys.all(),
      });
      onSuccess?.();
    },
    onError: () => {
      Toast.error("Failed to update prompt");
    },
  });
}

export function useDeleteSourceImagePresetMutation({
  teamExternalId,
  onSuccess,
}: {
  teamExternalId: string;
  onSuccess?: () => void;
}) {
  return useMutation({
    mutationKey:
      sourceImagePresetQueryKeys.deleteSourceImagePreset(teamExternalId),
    mutationFn: (presetExternalId: string) =>
      SourceImagePresetApi.deleteSourceImagePreset(
        teamExternalId,
        presetExternalId,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: sourceImagePresetQueryKeys.all(),
      });
      onSuccess?.();
    },
    onError: () => {
      Toast.error("Failed to delete prompt");
    },
  });
}

export function useSetSourceImagePresetFavouriteMutation({
  teamExternalId,
}: {
  teamExternalId: string;
}) {
  return useMutation({
    mutationKey:
      sourceImagePresetQueryKeys.setSourceImagePresetFavourite(teamExternalId),
    mutationFn: ({
      presetExternalId,
      isFavourite,
    }: {
      presetExternalId: string;
      isFavourite: boolean;
    }) =>
      SourceImagePresetApi.setSourceImagePresetFavourite(
        teamExternalId,
        presetExternalId,
        { isFavourite },
      ),
    onMutate: async ({ presetExternalId, isFavourite }) => {
      const queryKey = sourceImagePresetQueryKeys.all();
      await queryClient.cancelQueries({ queryKey });
      const snapshots = queryClient.getQueriesData<
        InfiniteData<PagedSourceImagePresetsResponseDto>
      >({ queryKey });
      queryClient.setQueriesData<
        InfiniteData<PagedSourceImagePresetsResponseDto>
      >({ queryKey }, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            presets: page.presets.map((preset) =>
              preset.externalId === presetExternalId
                ? { ...preset, isFavourite }
                : preset,
            ),
          })),
        };
      });
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshots) {
        for (const [key, data] of context.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      Toast.error("Failed to update favourite");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: sourceImagePresetQueryKeys.all(),
      });
    },
  });
}

export const sourceImagePresetQueryKeys = {
  all: () => ["sourceImagePreset"],
  paged: (
    teamExternalId: string,
    filterParams: SourceImagePresetsFilterParams,
    pageSize: number,
  ) => [
    "sourceImagePreset",
    "paged",
    { teamExternalId, ...filterParams, pageSize },
  ],
  updateSourceImagePreset: (teamExternalId: string) => [
    "sourceImagePreset",
    "update",
    { teamExternalId },
  ],
  deleteSourceImagePreset: (teamExternalId: string) => [
    "sourceImagePreset",
    "delete",
    { teamExternalId },
  ],
  setSourceImagePresetFavourite: (teamExternalId: string) => [
    "sourceImagePreset",
    "setFavourite",
    { teamExternalId },
  ],
};
